// ============ تتبع الزيارات (من غير كوكيز ومن غير بيانات شخصية) ============
// بيسجّل في Firestore (collection: stats، مستند لكل يوم بتوقيت دبي):
//   sessions (الزيارات)، pageviews، seconds (الوقت اللي الزوار قعدوه)، clicks (الكليكات حسب النوع)،
//   pages (أكتر الصفحات زيارة)، sources (جايين منين: جوجل، شات جي بي تي، انستجرام ...)، devices، langs
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isFirebaseConfigured, TRACK_LOCALHOST } from "../Data/firebaseConfig";
import { commit, dubaiDay, keyOf, statsWrite } from "./firestoreRest";
import { langFromPath, stripLang } from "./i18nPaths";

const SID_KEY = "hx-sid";
const SRC_KEY = "hx-src";
const FLUSH_EVERY = 120000; // كل دقيقتين بحد أقصى وهو فاتح الصفحة

// مصدر الزيارة من الـ referrer أو utm_source
const SOURCES = [
  ["google", /google\./],
  ["bing", /bing\.com/],
  ["yandex", /yandex\./],
  ["duckduckgo", /duckduckgo\./],
  ["chatgpt", /chatgpt\.com|openai\.com/],
  ["perplexity", /perplexity\.ai/],
  ["claude", /claude\.ai/],
  ["gemini", /gemini\.google/],
  ["copilot", /copilot\.microsoft/],
  ["facebook", /facebook\.com|fb\.com/],
  ["instagram", /instagram\.com/],
  ["tiktok", /tiktok\.com/],
  ["whatsapp", /whatsapp\.com|wa\.me/],
  ["linkedin", /linkedin\.com|lnkd\.in/],
  ["youtube", /youtube\.com|youtu\.be/],
  ["x", /(^|\.)x\.com|twitter\.com|t\.co/],
];

const detectSource = () => {
  try {
    const utm = new URLSearchParams(window.location.search).get("utm_source");
    if (utm) return keyOf(utm, "campaign");
    const ref = document.referrer ? new URL(document.referrer).hostname : "";
    if (!ref || ref === window.location.hostname) return "direct";
    if (/gemini\.google/.test(ref)) return "gemini";
    const hit = SOURCES.find(([, re]) => re.test(ref));
    return hit ? hit[0] : "other";
  } catch {
    return "direct";
  }
};

export const currentTrafficSource = () => {
  try {
    return sessionStorage.getItem(SRC_KEY) || detectSource();
  } catch {
    return detectSource();
  }
};

const deviceType = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobi/i.test(ua))) return "tablet";
  return /Mobi|Android|iPhone/i.test(ua) ? "mobile" : "desktop";
};

// نوع الكليك من العنصر اللي اتداس عليه
const clickType = (target) => {
  const el = target && target.closest && target.closest("a, button, summary, [role='button'], input[type='submit']");
  if (!el) return null;
  const href = el.getAttribute("href") || "";
  if (/wa\.me|whatsapp/i.test(href)) return "whatsapp";
  if (href.startsWith("tel:")) return "call";
  if (href.startsWith("mailto:")) return "email";
  if (el.type === "submit") return "form";
  if (el.closest(".langsw, .menu__langs, .foot__langs")) return "language";
  if (el.closest(".nav, .menu, .foot")) return "nav";
  if (el.classList.contains("btn") || el.closest(".dev, .pcard, .pj, .brow, .bcard")) return "cta";
  return "other";
};

const enabled = () => {
  if (typeof window === "undefined" || !isFirebaseConfigured()) return false;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  if (local && !TRACK_LOCALHOST) return false;
  if (!TRACK_LOCALHOST && navigator.webdriver) return false; // بوتات
  if (stripLang(window.location.pathname).startsWith("/admin")) return false;
  return true;
};

// العدادات اللي لسه متبعتتش
const pending = { seconds: 0, clicks: {} };
let visibleSince = null;
let lastFlush = Date.now();

const takeTime = () => {
  if (visibleSince != null) {
    pending.seconds += (Date.now() - visibleSince) / 1000;
    visibleSince = document.visibilityState === "visible" ? Date.now() : null;
  }
};

const flush = (keepalive = false) => {
  takeTime();
  const incs = {};
  const secs = Math.round(pending.seconds);
  if (secs >= 1) incs.seconds = Math.min(secs, 3600);
  Object.entries(pending.clicks).forEach(([k, n]) => (incs[`clicks.${k}`] = n));
  if (!Object.keys(incs).length) return;
  pending.seconds = 0;
  pending.clicks = {};
  lastFlush = Date.now();
  commit([statsWrite(incs)], { keepalive }).catch(() => {});
};

let started = false;
const start = () => {
  if (started) return;
  started = true;
  visibleSince = document.visibilityState === "visible" ? Date.now() : null;

  document.addEventListener(
    "click",
    (e) => {
      const type = clickType(e.target);
      if (!type) return;
      pending.clicks.total = (pending.clicks.total || 0) + 1;
      pending.clicks[type] = (pending.clicks[type] || 0) + 1;
      // كليكات التواصل بنبعتها على طول (الزائر غالبًا هيسيب الصفحة)
      if (type === "whatsapp" || type === "call" || type === "email") flush(true);
    },
    true
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
    else if (visibleSince == null) visibleSince = Date.now();
  });
  window.addEventListener("pagehide", () => flush(true));
  window.setInterval(() => {
    if (document.visibilityState === "visible" && Date.now() - lastFlush >= FLUSH_EVERY) flush();
  }, 30000);
};

// زيارة جديدة + صفحة اتفتحت
const recordPageview = (path) => {
  const incs = { pageviews: 1, [`pages.${keyOf(stripLang(path), "home")}`]: 1 };
  let isNew = false;
  try {
    if (!sessionStorage.getItem(SID_KEY)) {
      sessionStorage.setItem(SID_KEY, `${Date.now()}`);
      sessionStorage.setItem(SRC_KEY, detectSource());
      isNew = true;
    }
  } catch {
    isNew = true;
  }
  if (isNew) {
    incs.sessions = 1;
    incs[`devices.${deviceType()}`] = 1;
    incs[`langs.${langFromPath(path)}`] = 1;
    incs[`sources.${currentTrafficSource()}`] = 1;
  }
  commit([statsWrite(incs, dubaiDay())]).catch(() => {});
};

// بيتحط مرة واحدة جوه الـ Router (في App.js)
export const useTracking = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled()) return;
    start();
    recordPageview(window.location.pathname);
  }, [pathname]);
};

export const Tracker = () => {
  useTracking();
  return null;
};
